import React, { useEffect, useRef } from 'react';
import {
  Box,
  Stack,
  TextField,
  Menu,
  MenuItem,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Button,
  CircularProgress,
  Backdrop,
  InputAdornment,
} from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { useBizview } from './Hooks/UseBizview';
import dayjs from 'dayjs';
import EWayCustomDate from '../../Date/eWayCustomDate';
import { DateRangeRsuite } from '../../_components';

export default function PeriodSelector({ onChange, preview, defaultOffsetMonths = 0, onValidateSelect, value = null }) {
  const {
    fy,
    anchorEl,
    subAnchorEl,
    selectedType,
    selectedVal,
    customOpen,
    financialYears,
    periodTypes,
    customDates,
    openMainMenu,
    closeAll,
    handleType,
    handleSubSelect,
    handleCustomDateChange,
    onFyChange,
    setCustomOpen,
    isLoading,
    hasError,
    isCommonLoading,
  } = useBizview(defaultOffsetMonths, onValidateSelect, value);

  const DateRangeRef = useRef(null);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const lastUpdateRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onValidateSelectRef = useRef(onValidateSelect);
  const isSyncingFromExternalRef = useRef(false);

  // Keep onChangeRef up to date without causing re-renders
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Keep onValidateSelectRef up to date without causing re-renders
  useEffect(() => {
    onValidateSelectRef.current = onValidateSelect;
  }, [onValidateSelect]);

  // Track when value prop changes to detect external sync
  useEffect(() => {
    if (value && value.financialyearid && value.startdate && value.enddate) {
      isSyncingFromExternalRef.current = true;
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isSyncingFromExternalRef.current = false;
      }, 200);
    }
  }, [value]);

  // Reset dedup key when selection is cleared so re-selecting same period updates
  useEffect(() => {
    if (!selectedType || !selectedVal) {
      lastUpdateRef.current = null;
    }
  }, [selectedType, selectedVal]);

  useEffect(() => {
    // Early return if loading, no onChange callback, or if syncing from external value
    if (isLoading || !onChangeRef.current || isSyncingFromExternalRef.current) return;

    const clear = () =>
      onChangeRef.current((prev) => ({
        ...prev,
        financialyearid: null,
        startdate: null,
        enddate: null,
        companyid: null,
      }));

    if (!fy) {
      clear();
      return;
    }

    if (!selectedType) return;

    let selected = null;

    if (selectedType === 'custom') {
      const s = customDates?.start;
      const e = customDates?.end;
      if (s && e) {
        selected = { start: s, end: e };
      }
    } else {
      const typeObj = periodTypes?.find((p) => p?.value === selectedType);
      if (typeObj?.children) {
        selected = typeObj.children?.find((item) => item?.label === selectedVal) ?? null;
      } else if (typeObj?.value === 'fullyear') {
        selected = { start: fy?.startTimestamp, end: fy?.endTimestamp };
      }
    }

    if (!selected && !fy) return;

    // Check if we should update
    const currentKey = `${fy?.value}-${selected?.start}-${selected?.end}`;
    if (lastUpdateRef.current === currentKey) return;

    // Validate before calling onChange if validator exists
    if (typeof onValidateSelectRef.current === 'function') {
      const candidate = {
        financialyearid: fy?.value,
        startdate: selected?.start,
        enddate: selected?.end,
      };
      const isValid = onValidateSelectRef.current(candidate);
      if (!isValid) {
        // Validation failed, don't update
        return;
      }
    }

    lastUpdateRef.current = currentKey;

    onChangeRef.current((prev) => ({
      ...prev,
      financialyearid: fy?.value,
      startdate: selected?.start,
      enddate: selected?.end,
      companyid: fy?.companyid,
    }));
  }, [fy, selectedVal, selectedType, customDates, periodTypes, isLoading]);

  return (
    <>
      {/* Common Backdrop Loading */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isCommonLoading}
      >
        <CircularProgress color="inherit" size={40} />
      </Backdrop>

      <Box sx={{}}>
        <Stack direction={{xs:"row",sm:"row"}} spacing={{xs:2,sm:1}}  sx={{ width: '100%' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: {xs:'48%', sm:'50%',lg:'60%'} }}
            options={financialYears}
            value={fy}
            onChange={onFyChange}
            disabled={isCommonLoading}
            renderInput={(params) => <TextField {...params} label={defaultOffsetMonths === 1 ? 'Compare' : 'Financial Year'} />}
          />

          <TextField
            size="small"
            label="Period"
            sx={{ width: {xs:'48%', sm:'50%',lg:'60%'}, cursor: 'pointer' }}
            value={selectedVal || periodTypes?.find((p) => p?.value === selectedType)?.label || ''}
            onClick={openMainMenu}
            inputProps={{ readOnly: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" onClick={openMainMenu} sx={{ cursor: 'pointer' }}>
                  <ArrowDropDownRoundedIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
            disabled={!fy || isCommonLoading}
            placeholder="Select Period"
          />

          {/* <Box
          sx={{
            borderRadius: 1,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.875rem',
            color: 'rgba(0, 0, 0, 0.87)',
            backgroundColor: '#fff',
          }}
        >
          {preview}
        </Box> */}
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => closeAll('all')}
          PaperProps={{ elevation: 4, sx: { borderRadius: 2, minWidth: 180 } }}
        >
          {periodTypes.map(({ value, label }) => (
            <MenuItem key={value} onClick={handleType(value)}>
              {label}
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={subAnchorEl}
          open={Boolean(subAnchorEl)}
          onClose={() => closeAll('sub')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            elevation: 4,
            sx: {
              borderRadius: 2,
              minWidth: 160,
              ...(customOpen && { padding: 0 }),
            },
          }}
        >
          {customOpen ? (
            <Box sx={{ p: 0.5 }}>
              <DateRangeRsuite
                ref={DateRangeRef}
                value={[customDates?.start ? customDates.start : null, customDates?.start && customDates?.end ? customDates.end : null]}
                shouldDisableDate={(date) => {
                  const startDate = fy?.startTimestamp;
                  const endDate = fy?.endTimestamp;
                  if (!startDate || !endDate) return false;
                  const dateTime = date.getTime() / 1000;
                  return dateTime < startDate || dateTime > endDate;
                }}
                onChange={(e) => handleCustomDateChange(e)}
              />
            </Box>
          ) : (
            periodTypes?.find((p) => p?.value === selectedType)
              ?.children?.map((opt) => (
                <MenuItem key={opt.value} onClick={() => handleSubSelect(opt?.label, selectedType)}>
                  {opt?.label}
                </MenuItem>
              ))
          )}
        </Menu>
      </Box>
    </>
  );
}
